import { useState, useEffect, useMemo, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { getExecutions } from '../api/executions';
import { getFlows } from '../api/flows';
import { ExecutionResponse } from '../types';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PageHeader } from '../components/PageHeader';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { cn } from '../lib/utils';
import { STATUS_BADGE } from '../lib/executionStatus';


type SortKey = 'startedAt' | 'duration' | 'status';
type SortDir = 'asc' | 'desc';

const FILTER_STORAGE_KEY = 'executionsPage.filters';
const PREVIEW_MAX_LENGTH = 60;
const MAX_EXPAND_DEPTH = 6;

type StoredFilters = {
  flowId: string;
  status: string;
  customerId: string;
  startedFrom: string;
  startedTo: string;
  onlyWithError: boolean;
  errorContains: string;
  sortBy: SortKey;
  sortDir: SortDir;
};

function loadStoredFilters(): Partial<StoredFilters> {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<StoredFilters>) : {};
  } catch {
    return {};
  }
}

function previewFromPayload(inboundPayload: string | undefined): string {
  if (!inboundPayload) return '';
  try {
    const parsed = JSON.parse(inboundPayload);
    const body = parsed?.body;
    if (body == null || typeof body !== 'object') return '';
    const parts: string[] = [];
    for (const [k, v] of Object.entries(body)) {
      if (parts.length >= 2) break;
      if (v == null) continue;
      if (typeof v === 'object') continue;
      parts.push(`${k}: ${String(v)}`);
    }
    const joined = parts.join(', ');
    return joined.length > PREVIEW_MAX_LENGTH
      ? joined.slice(0, PREVIEW_MAX_LENGTH - 1) + '…'
      : joined;
  } catch {
    return '';
  }
}

function SortHeader({
  label,
  sortKey,
  active,
  dir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const isActive = active === sortKey;
  return (
    <TableHead className="p-0">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="flex h-9 w-full items-center gap-1 px-3 text-left hover:text-foreground"
      >
        {label}
        {isActive ? (
          dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-50" />
        )}
      </button>
    </TableHead>
  );
}

function ExecutionRow({
  ex,
  depth,
  expanded,
  onToggleExpand,
}: {
  ex: ExecutionResponse;
  depth: number;
  expanded: boolean;
  onToggleExpand: (id: string) => void;
}) {
  const durationMs = ex.completedAt
    ? new Date(ex.completedAt).getTime() - new Date(ex.startedAt).getTime()
    : null;
  const displayLabel = ex.label && ex.label.trim().length > 0
    ? ex.label
    : previewFromPayload(ex.inboundPayload);
  const indent = depth > 0 ? { paddingLeft: `${depth * 1.5 + 0.75}rem` } : undefined;

  return (
    <TableRow>
      <TableCell className="font-medium" style={indent}>
        <span className="inline-flex items-center gap-1.5">
          {ex.childCount > 0 && depth < MAX_EXPAND_DEPTH - 1 ? (
            <button
              onClick={() => onToggleExpand(ex.id)}
              className="text-muted-foreground hover:text-foreground w-4 inline-flex items-center justify-center"
              aria-label={expanded ? 'Collapse children' : 'Expand children'}
            >
              {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </button>
          ) : (
            <span className="w-4" />
          )}
          {ex.flowName ?? ex.flowId}
          {ex.childCount > 0 && (
            <span className="text-xs text-muted-foreground ml-1">({ex.childCount})</span>
          )}
        </span>
      </TableCell>
      <TableCell className="text-foreground/80 max-w-[260px] truncate" title={displayLabel}>
        {displayLabel || <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="text-muted-foreground font-mono text-xs">{ex.customerId ?? '—'}</TableCell>
      <TableCell>
        <span className={cn('inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium', STATUS_BADGE[ex.status])}>
          {ex.status}
        </span>
      </TableCell>
      <TableCell className="text-muted-foreground">{new Date(ex.startedAt).toLocaleString()}</TableCell>
      <TableCell className="text-muted-foreground">
        {durationMs != null ? `${durationMs}ms` : '—'}
      </TableCell>
      <TableCell>
        <div className="flex gap-2 text-xs">
          <Link to={`/executions/${ex.id}`} className="text-primary hover:underline">
            View
          </Link>
          <Link
            to={`/flows/${ex.flowId}?execution=${ex.id}`}
            className="text-primary hover:underline"
            title="Open the flow editor with this execution overlaid"
          >
            Debug
          </Link>
        </div>
      </TableCell>
    </TableRow>
  );
}

function ChildRows({
  parentId,
  depth,
  expanded,
  onToggleExpand,
}: {
  parentId: string;
  depth: number;
  expanded: Set<string>;
  onToggleExpand: (id: string) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['executions', 'children', parentId],
    queryFn: () => getExecutions({
      parentExecutionId: parentId,
      topLevelOnly: false,
      pageSize: 100,
      sortBy: 'startedAt',
      sortDir: 'asc',
    }),
  });

  if (isLoading) {
    return (
      <TableRow className="bg-muted/30">
        <TableCell colSpan={7} className="py-2 text-xs text-muted-foreground" style={{ paddingLeft: `${depth * 1.5 + 0.75}rem` }}>
          Loading children…
        </TableCell>
      </TableRow>
    );
  }

  if (!data?.items.length) {
    return (
      <TableRow className="bg-muted/30">
        <TableCell colSpan={7} className="py-2 text-xs text-muted-foreground" style={{ paddingLeft: `${depth * 1.5 + 0.75}rem` }}>
          No children.
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {data.items.map(child => (
        <Fragment key={child.id}>
          <ExecutionRow
            ex={child}
            depth={depth}
            expanded={expanded.has(child.id)}
            onToggleExpand={onToggleExpand}
          />
          {expanded.has(child.id) && depth < MAX_EXPAND_DEPTH - 1 && (
            <ChildRows
              parentId={child.id}
              depth={depth + 1}
              expanded={expanded}
              onToggleExpand={onToggleExpand}
            />
          )}
        </Fragment>
      ))}
    </>
  );
}

export function ExecutionsPage() {
  const qc = useQueryClient();
  const stored = loadStoredFilters();

  const [flowId, setFlowId]             = useState(stored.flowId ?? '');
  const [status, setStatus]             = useState(stored.status ?? '');
  const [customerIdInput, setCustomerIdInput] = useState(stored.customerId ?? '');
  const [customerId, setCustomerId]     = useState(stored.customerId ?? '');
  const [startedFrom, setStartedFrom]   = useState(stored.startedFrom ?? '');
  const [startedTo, setStartedTo]       = useState(stored.startedTo ?? '');
  const [onlyWithError, setOnlyWithError] = useState(stored.onlyWithError ?? false);
  const [errorContainsInput, setErrorContainsInput] = useState(stored.errorContains ?? '');
  const [errorContains, setErrorContains] = useState(stored.errorContains ?? '');

  const [sortBy, setSortBy]   = useState<SortKey>(stored.sortBy ?? 'startedAt');
  const [sortDir, setSortDir] = useState<SortDir>(stored.sortDir ?? 'desc');

  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    const payload: StoredFilters = {
      flowId, status, customerId, startedFrom, startedTo,
      onlyWithError, errorContains, sortBy, sortDir,
    };
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(payload));
  }, [flowId, status, customerId, startedFrom, startedTo, onlyWithError, errorContains, sortBy, sortDir]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCustomerId(customerIdInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [customerIdInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setErrorContains(errorContainsInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [errorContainsInput]);

  function handleSort(key: SortKey) {
    if (key === sortBy) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
    setPage(1);
  }

  function handleClear() {
    setFlowId('');
    setStatus('');
    setCustomerIdInput('');
    setCustomerId('');
    setStartedFrom('');
    setStartedTo('');
    setOnlyWithError(false);
    setErrorContainsInput('');
    setErrorContains('');
    setSortBy('startedAt');
    setSortDir('desc');
    setPage(1);
  }

  function toggleExpanded(id: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const hasActiveFilter = flowId || status || customerIdInput
    || startedFrom || startedTo || onlyWithError || errorContainsInput;

  const startedFromIso = useMemo(
    () => startedFrom ? new Date(startedFrom).toISOString() : undefined,
    [startedFrom]);
  const startedToIso = useMemo(
    () => startedTo ? new Date(startedTo).toISOString() : undefined,
    [startedTo]);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['executions',
      flowId, status, customerId, startedFromIso, startedToIso,
      onlyWithError, errorContains, sortBy, sortDir, page],
    queryFn: () => getExecutions({
      flowId: flowId || undefined,
      status: status || undefined,
      customerId: customerId || undefined,
      startedFrom: startedFromIso,
      startedTo: startedToIso,
      hasError: onlyWithError ? true : undefined,
      errorContains: errorContains || undefined,
      topLevelOnly: true,
      sortBy,
      sortDir,
      page,
      pageSize: 20,
    }),
  });

  const { data: flows } = useQuery({
    queryKey: ['flows'],
    queryFn: getFlows,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Executions"
        description="Every run of every flow, newest first."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              refetch();
              qc.invalidateQueries({ queryKey: ['executions', 'children'] });
            }}
            disabled={isFetching}
            title="Reload executions"
          >
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            {isFetching ? 'Reloading…' : 'Reload'}
          </Button>
        }
      />

      <div className="flex flex-wrap gap-2 items-center bg-card border border-border rounded-lg p-3">
        <Input
          type="text"
          placeholder="Customer ID"
          value={customerIdInput}
          onChange={e => { setCustomerIdInput(e.target.value); setPage(1); }}
          className="h-9 w-44"
        />

        <Select value={status || 'all'} onValueChange={v => { setStatus(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="h-9 w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {['Queued', 'Running', 'Completed', 'Failed', 'Retried', 'Skipped'].map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={flowId || 'all'} onValueChange={v => { setFlowId(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="h-9 w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All flows</SelectItem>
            {flows?.map(f => (
              <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          From
          <input
            type="datetime-local"
            value={startedFrom}
            onChange={e => { setStartedFrom(e.target.value); setPage(1); }}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>

        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          To
          <input
            type="datetime-local"
            value={startedTo}
            onChange={e => { setStartedTo(e.target.value); setPage(1); }}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>

        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={onlyWithError}
            onChange={e => { setOnlyWithError(e.target.checked); setPage(1); }}
          />
          Only with error
        </label>

        <Input
          type="text"
          placeholder="Error contains…"
          value={errorContainsInput}
          onChange={e => { setErrorContainsInput(e.target.value); setPage(1); }}
          className="h-9 w-44"
        />

        {hasActiveFilter && (
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={handleClear}>
            Clear
          </Button>
        )}
      </div>

      {isLoading ? <p className="text-muted-foreground">Loading…</p> : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Flow</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Customer</TableHead>
                <SortHeader label="Status"  sortKey="status"    active={sortBy} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Started" sortKey="startedAt" active={sortBy} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Duration" sortKey="duration" active={sortBy} dir={sortDir} onSort={handleSort} />
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.items.map(ex => (
                <Fragment key={ex.id}>
                  <ExecutionRow
                    ex={ex}
                    depth={0}
                    expanded={expanded.has(ex.id)}
                    onToggleExpand={toggleExpanded}
                  />
                  {expanded.has(ex.id) && (
                    <ChildRows
                      parentId={ex.id}
                      depth={1}
                      expanded={expanded}
                      onToggleExpand={toggleExpanded}
                    />
                  )}
                </Fragment>
              ))}
              {data?.items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="p-6 text-center text-muted-foreground">No executions</TableCell></TableRow>
              )}
            </TableBody>
          </Table>

          {data && data.total > data.pageSize && (
            <div className="flex gap-2 justify-end items-center text-sm">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                ← Prev
              </Button>
              <span className="px-2 text-muted-foreground">
                Page {page} of {Math.ceil(data.total / data.pageSize)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= Math.ceil(data.total / data.pageSize)}
                onClick={() => setPage(p => p + 1)}
              >
                Next →
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
